import Chip from '@components/Chip';
import Column from '@components/Column';
import Row from '@components/Row';
import Text from '@components/Text';
import TextField from '@components/TextField';
import Tooltip from '@components/Tooltip';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import Info from '@icons/info/Info';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const PRESET_AMOUNTS = [1, 3, 5]; // 만원

interface Props {
    /** 만원 단위 */
    amountMan: number;
    onChangeMan: (man: number) => void;
    errorText?: string;
    placeholder: string;
    tooltipDaily: string;
    tooltipMaxBalance: string;
}

function PrepaidAmountInput({
    amountMan,
    onChangeMan,
    errorText,
    placeholder,
    tooltipDaily,
    tooltipMaxBalance,
}: Props) {
    const { t } = useTranslation('prepaidCard');
    const [inputValue, setInputValue] = useState(amountMan > 0 ? String(amountMan) : '');
    const [infoOpen, setInfoOpen] = useState(false);
    const infoRef = useRef<HTMLDivElement>(null);
    const selectedPreset = PRESET_AMOUNTS.find((v) => v === amountMan);

    const applyAmount = (man: number) => {
        const next = Math.max(man, 0);
        onChangeMan(next);
        setInputValue(next > 0 ? String(next) : '');
    };

    useEffect(() => {
        setInputValue(amountMan > 0 ? String(amountMan) : '');
    }, [amountMan]);

    useEffect(() => {
        if (!infoOpen) return undefined;
        const handleOutside = (e: MouseEvent) => {
            if (infoRef.current && !infoRef.current.contains(e.target as Node)) {
                setInfoOpen(false);
            }
        };
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [infoOpen]);

    return (
        <Container fullWidth as='section' spacing={4}>
            <TitleRow spacing={1} align='center'>
                <Text size='title-lg' fontWeight='bold' color={colors.gray8}>
                    {t('charge.chargeAmount')}
                </Text>
                <InfoWrap ref={infoRef}>
                    <InfoButton type='button'
                        aria-label='info'
                        aria-expanded={infoOpen}
                        onClick={() => setInfoOpen((v) => !v)}>
                        <Info size={24} color={colors.gray6} />
                    </InfoButton>
                    {infoOpen && (
                        <Tooltip multiline
                            align='left'
                            top='unset'
                            right='unset'
                            bottom='calc(100% + 4px)'
                            left={-3}
                            onClose={() => setInfoOpen(false)}>
                            <BulletList>
                                <li>{tooltipDaily}</li>
                                <li>{tooltipMaxBalance}</li>
                            </BulletList>
                        </Tooltip>
                    )}
                </InfoWrap>
            </TitleRow>
            <AmountChips>
                {PRESET_AMOUNTS.map((man) => (
                    <AmountChip key={man}
                        isSelected={selectedPreset === man}
                        onClick={() => applyAmount(man)}>
                        {man}{t('components.amount.tenThousandUnit')}
                    </AmountChip>
                ))}
            </AmountChips>
            <InputRow fullWidth spacing={2} align='flex-start'>
                <FieldWrap>
                    <TextField
                        placeholder={placeholder}
                        value={inputValue}
                        errorText={errorText}
                        clearable
                        onChange={(e) => {
                            const raw = e.target.value.replace(/[^0-9]/g, '');
                            const man = Number(raw) || 0;
                            setInputValue(raw);
                            onChangeMan(man);
                        }}
                        inputMode='numeric'
                    />
                </FieldWrap>
                <Unit size='body-long' fontWeight='regular' color={colors.textSecondary}>
                    {t('components.amount.tenThousandUnit')}
                </Unit>
            </InputRow>
        </Container>
    );
}

const Container = styled(Column)`
    padding: var(--spacing-4);
`;

const TitleRow = styled(Row)``;

const InfoWrap = styled.div`
    position: relative;
`;

const InfoButton = styled.button`
    align-items: center;
    background: transparent;
    display: flex;
    padding: 0;
`;

const BulletList = styled.ul`
    display: flex;
    flex-direction: column;
    gap: var(--spacing-1);
    list-style: disc;
    margin: 0;
    padding-left: var(--spacing-4);
`;

const AmountChips = styled(Chip.Container)`
    width: 100%;
`;

const AmountChip = styled(Chip)`
    flex: 1;
`;

const InputRow = styled(Row)``;

const FieldWrap = styled.div`
    flex: 1;
    min-width: 0;
`;

const Unit = styled(Text)`
    flex-shrink: 0;
    height: 40px;
    line-height: 40px;
`;
export default PrepaidAmountInput;
